import { useAtomValue, useSetAtom } from 'jotai';
import { useSearchParams } from 'react-router';

import { Tag } from '@data';
import { useTranslate } from '@i18n';
import { useTags } from '@queries';
import { ActivityFilter, activityFilterAtom, updateActivityFilterAtom } from '@store';
import { Box, CloseIcon, Heading4, Paragraph, Select, u } from '@ui';

import * as css from '../ActivityFilter.css';

type SelectedTagProps = {
    updateFilter: (updates: Partial<ActivityFilter>) => void;
    selectedTags: number[];
    tag: Tag;
};

function SelectedTag({ updateFilter, selectedTags, tag }: SelectedTagProps) {
    const t = useTranslate();

    if (!selectedTags.includes(tag.id)) {
        return null;
    }

    return (
        <button
            type="button"
            aria-label={t('activity_filter.remove_tag').replace('{tag}', tag.name)}
            className={css.selectedTag}
            onClick={() => {
                const tags = selectedTags.filter((tagId) => tagId !== tag.id);
                updateFilter({ tags: tags.length ? tags : undefined });
            }}
        >
            {tag.name}
            <CloseIcon size={16} aria-hidden />
        </button>
    );
}

export function ActivityTagFilter() {
    const t = useTranslate();
    const [searchParams] = useSearchParams();
    const { data: tags, isPending, isError } = useTags(searchParams.get('studentId') ?? undefined);
    const filter = useAtomValue(activityFilterAtom);
    const updateFilter = useSetAtom(updateActivityFilterAtom);
    const selectedTags = filter.tags ?? [];

    return (
        <Box display="flex" flexDirection="column" gap={u(1)}>
            <Heading4>{t('activity_filter.tags')}</Heading4>
            <Select
                options={tags?.map((tag) => ({ value: String(tag.id), label: tag.name })) ?? []}
                selectedValues={selectedTags.map(String)}
                onChange={(values) => {
                    updateFilter({ tags: values.length ? values.map(Number) : undefined });
                }}
                placeholder={t('activity_filter.add_tag')}
                label={t('activity_filter.tags')}
                width="100%"
                multiple
                searchBox
                autoSelect={false}
                disabled={isPending || isError || !tags?.length}
            />
            {isError && <Paragraph>{t('activity_filter.tags_load_error')}</Paragraph>}
            {!isPending && !isError && !tags?.length && <Paragraph>{t('activity_filter.no_tags')}</Paragraph>}
            {!!selectedTags.length && (
                <Box display="flex" flexWrap="wrap" gap={u(1)}>
                    {tags?.map((tag) => (
                        <SelectedTag key={tag.id} tag={tag} selectedTags={selectedTags} updateFilter={updateFilter} />
                    ))}
                </Box>
            )}
        </Box>
    );
}
